import { trpc, queryClient } from "~/utils/trpc";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { useState, useEffect } from "react";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Textarea } from "@quake/shared-ui/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { useHasPermission } from "~/store/usePermissionStore";
import { cn } from "@quake/shared-ui/lib/utils";
import { motion, AnimatePresence } from "motion/react";
import BanIcon from "lucide-react/dist/esm/icons/ban";
import CalendarDaysIcon from "lucide-react/dist/esm/icons/calendar-days";
import HistoryIcon from "lucide-react/dist/esm/icons/history";
import SaveIcon from "lucide-react/dist/esm/icons/save";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import { format } from "date-fns";

type StandardFolder = "inbox" | "sent" | "drafts" | "junk" | "deleted";
type FolderRule = { provider: "outlook"; mailboxId: string; folderId: string };

type SyncMode = "none" | "from_date" | "from_record_creation";

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/email-settings/sync-settings/")({
  async beforeLoad({ params }) {
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions());
    const permissions = session?.user?.permissions ?? [];
    const hasPermission =
      session?.user?.role === "Administrator" ||
      permissions.some(
        (p) =>
          (p.resource === "emailSyncSettings" && (p.action === "view" || p.action === "update" || p.action === "*")) ||
          (p.resource === "*" && p.action === "*"),
      );
    if (!hasPermission) {
      throw redirect({ to: "/$datasetSlug/dashboard", params: { datasetSlug: params.datasetSlug } });
    }
  },
  component: RouteComponent,
});

const SYNC_MODES = [
  {
    value: "none" as SyncMode,
    label: "No date filter",
    description: "Sync all available emails with no start date restriction.",
    icon: BanIcon,
  },
  {
    value: "from_date" as SyncMode,
    label: "Specific date",
    description: "Only sync emails sent or received on or after a chosen date.",
    icon: CalendarDaysIcon,
  },
  {
    value: "from_record_creation" as SyncMode,
    label: "Record creation",
    description: "Sync emails from the date the contact or candidate record was created.",
    icon: HistoryIcon,
  },
];

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function RouteComponent() {
  const { datasetSlug } = Route.useParams();
  const maxSyncFromDate = format(new Date(), "yyyy-MM-dd");
  const { data, isLoading, error: settingsError } = useQuery(trpc.emailSettings.getSyncSettings.queryOptions());
  const canUpdate = useHasPermission("emailSyncSettings", "update");

  const [mailboxQuery, setMailboxQuery] = useState("");
  const [mailboxId, setMailboxId] = useState("");
  const [folderQuery, setFolderQuery] = useState("");
  const [excludedStandardFolders, setExcludedStandardFolders] = useState<StandardFolder[]>([
    "drafts",
    "junk",
    "deleted",
  ]);
  const [excludedFolders, setExcludedFolders] = useState<FolderRule[]>([]);
  const [ignoredPatternsText, setIgnoredPatternsText] = useState("");
  const excludedLabels = useQuery(trpc.emailSettings.getExcludedFolderLabels.queryOptions());
  const mailboxes = useQuery(trpc.emailSettings.listSyncMailboxes.queryOptions({ query: mailboxQuery }));
  const folders = useQuery(trpc.emailSettings.getSyncFolders.queryOptions({ mailboxId }, { enabled: !!mailboxId }));
  const [syncMode, setSyncMode] = useState<SyncMode>("none");
  const [syncFromDate, setSyncFromDate] = useState<string>("");

  useEffect(() => {
    const normalizedMode = data?.syncMode ?? "none";
    const candidateDate = data?.syncFromDate ?? "";
    const match = DATE_REGEX.exec(candidateDate);
    const normalizedDate = match ? match[0] : "";

    setExcludedStandardFolders(data?.excludedStandardFolders ?? ["drafts", "junk", "deleted"]);
    setExcludedFolders(data?.excludedFolders ?? []);
    setIgnoredPatternsText((data?.ignoredAddressPatterns ?? []).join("\n"));
    setSyncMode(normalizedMode);
    setSyncFromDate(normalizedDate);
  }, [data]);

  useBreadCrumbs([
    { title: "Email Settings", link: `/${datasetSlug}/settings/email-settings` },
    { title: "Sync Settings", link: `/${datasetSlug}/settings/email-settings/sync-settings` },
  ]);

  const updateMutation = useMutation(
    trpc.emailSettings.updateSyncSettings.mutationOptions({
      onSuccess: () => {
        toast.success("Sync settings saved");
        queryClient.invalidateQueries({ queryKey: trpc.emailSettings.getSyncSettings.pathKey() });
        queryClient.invalidateQueries({ queryKey: trpc.emailSettings.getSyncFolders.pathKey() });
        queryClient.invalidateQueries({ queryKey: trpc.emailSettings.getExcludedFolderLabels.pathKey() });
      },
      onError: (err) => {
        toast.error(err.message ?? "Failed to save settings");
      },
    }),
  );

  const handleSave = () => {
    updateMutation.mutate({
      syncMode,
      excludedStandardFolders,
      excludedFolders,
      ignoredAddressPatterns: ignoredPatternsText
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean),
      syncFromDate: syncMode === "from_date" ? syncFromDate || null : null,
    });
  };

  const isSyncFromDateValid = DATE_REGEX.test(syncFromDate);
  const isFormValid = syncMode !== "from_date" || isSyncFromDateValid;

  return (
    <div className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="space-y-1">
        <h2 className="text-xl font-bold tracking-tight">Email Sync Settings</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Optimize your workspace by controlling which emails are synced into Quake.
        </p>
      </div>

      <Card className="border-border shadow-sm overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Sync Strategy</CardTitle>
          <CardDescription className="text-sm">
            Choose a starting point for email synchronization. This setting applies to all integrated email accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-xl bg-muted/50 border border-border" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4">
              {SYNC_MODES.map((mode) => {
                const Icon = mode.icon;
                const isSelected = syncMode === mode.value;

                return (
                  <div key={mode.value} className="relative group">
                    <div
                      role="button"
                      tabIndex={canUpdate ? 0 : -1}
                      aria-disabled={!canUpdate}
                      aria-pressed={isSelected}
                      onClick={() => canUpdate && setSyncMode(mode.value)}
                      onKeyDown={(e) => {
                        if (!canUpdate) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSyncMode(mode.value);
                        }
                      }}
                      className={cn(
                        "w-full flex items-start gap-4 rounded-xl border-2 p-5 text-left transition-all duration-200 outline-none ring-offset-background cursor-pointer",
                        isSelected
                          ? "border-primary bg-primary/[0.03] shadow-inner"
                          : "border-border bg-background hover:border-border-hover hover:bg-muted/30 hover:shadow-sm",
                        !canUpdate && "opacity-50 cursor-not-allowed",
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200",
                          isSelected
                            ? "border-primary/20 bg-primary/10 text-primary"
                            : "border-border bg-muted/30 text-muted-foreground group-hover:bg-muted group-hover:text-foreground",
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>

                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={cn(
                              "font-semibold text-sm transition-colors duration-200",
                              isSelected ? "text-primary" : "text-foreground",
                            )}
                          >
                            {mode.label}
                          </span>
                          <AnimatePresence>
                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.5, opacity: 0 }}
                              >
                                <CheckIcon className="h-4 w-4 text-primary" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{mode.description}</p>

                        <AnimatePresence>
                          {mode.value === "from_date" && isSelected && (
                            <motion.div
                              initial={{ height: 0, opacity: 0, marginTop: 0 }}
                              animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                              exit={{ height: 0, opacity: 0, marginTop: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pb-2">
                                <label
                                  htmlFor="sync-from-date"
                                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2"
                                >
                                  Select sync start date
                                </label>
                                <Input
                                  id="sync-from-date"
                                  type="date"
                                  value={syncFromDate}
                                  onChange={(e) => setSyncFromDate(e.target.value)}
                                  className="w-full sm:w-64 bg-background border-primary/20 focus:border-primary focus:ring-primary/20 transition-all font-medium"
                                  max={maxSyncFromDate}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <section className="space-y-2 border-t pt-4" aria-label="Ignored email addresses">
            <label htmlFor="ignored-address-patterns" className="text-sm font-semibold">
              Ignored email addresses
            </label>
            <p id="ignored-address-description" className="text-xs text-muted-foreground">
              Skip the entire message when From, Sender, To, Cc, or Bcc matches. Add one address, domain, or * pattern
              per line, up to 200 entries. Existing imported emails stay available.
            </p>
            <Textarea
              id="ignored-address-patterns"
              aria-describedby="ignored-address-description"
              className="min-h-28 text-sm"
              rows={4}
              value={ignoredPatternsText}
              disabled={!canUpdate || isLoading || !!settingsError || updateMutation.isPending}
              placeholder={"person@example.com\nsome-bank.com\nprivate-*@example.com"}
              onChange={(event) => setIgnoredPatternsText(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Addresses belonging to users in this workspace are automatically excluded from contact and candidate
              matching. Other addresses on the same record remain eligible.
            </p>
            {settingsError && (
              <p role="alert" className="text-xs text-destructive">
                Could not load sync settings.
              </p>
            )}
            {updateMutation.error && (
              <p role="alert" className="text-xs text-destructive">
                {updateMutation.error.message}
              </p>
            )}
          </section>

          <section className="space-y-3 border-t pt-4" aria-label="Excluded folders">
            <div>
              <h3 className="text-sm font-semibold">Excluded folders</h3>
              <p className="text-xs text-muted-foreground">
                Excluded folders and their descendants stop importing. Existing history stays available.
              </p>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {(["inbox", "sent", "drafts", "junk", "deleted"] as const).map((folder) => (
                <label key={folder} className="flex cursor-pointer items-center gap-2 text-sm capitalize">
                  <input
                    type="checkbox"
                    disabled={!canUpdate}
                    checked={excludedStandardFolders.includes(folder)}
                    onChange={(event) =>
                      setExcludedStandardFolders((current) =>
                        event.target.checked ? [...current, folder] : current.filter((item) => item !== folder),
                      )
                    }
                  />
                  {folder === "deleted" ? "Deleted items" : folder}
                </label>
              ))}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                aria-label="Search mailboxes"
                placeholder="Search mailboxes"
                value={mailboxQuery}
                onChange={(event) => setMailboxQuery(event.target.value)}
              />
              <select
                aria-label="Mailbox"
                className="h-9 cursor-pointer rounded-md border bg-background px-3 text-sm"
                value={mailboxId}
                onChange={(event) => setMailboxId(event.target.value)}
              >
                <option value="">Select a mailbox</option>
                {mailboxes.data?.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.address}
                  </option>
                ))}
              </select>
            </div>
            {mailboxes.error && (
              <p role="alert" className="text-sm text-destructive">
                Could not load mailboxes.
              </p>
            )}
            {mailboxId && (
              <div className="space-y-2">
                <Input
                  aria-label="Search folders"
                  placeholder="Search folders"
                  value={folderQuery}
                  onChange={(event) => setFolderQuery(event.target.value)}
                />
                {folders.isLoading && <p className="text-xs text-muted-foreground">Loading cached folders…</p>}
                {folders.error && (
                  <p role="alert" className="text-sm text-destructive">
                    Could not load folders.
                  </p>
                )}
                <div className="max-h-64 overflow-auto divide-y rounded-md border">
                  {folders.data
                    ?.filter((folder) => folder.path.toLowerCase().includes(folderQuery.toLowerCase()))
                    .map((folder) => {
                      const selected = excludedFolders.some(
                        (rule) => rule.mailboxId === mailboxId && rule.folderId === folder.id,
                      );
                      return (
                        <label key={folder.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            disabled={!canUpdate}
                            checked={selected}
                            onChange={(event) =>
                              setExcludedFolders((current) =>
                                event.target.checked
                                  ? [...current, { provider: "outlook", mailboxId, folderId: folder.id }]
                                  : current.filter(
                                      (rule) => rule.mailboxId !== mailboxId || rule.folderId !== folder.id,
                                    ),
                              )
                            }
                          />
                          <span className="min-w-0 truncate">{folder.path}</span>
                          {!folder.available && <span className="text-xs text-muted-foreground">Unavailable</span>}
                          {folder.excluded && !selected && (
                            <span className="text-xs text-muted-foreground">Excluded by policy</span>
                          )}
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
            {excludedFolders.length > 0 && (
              <ul className="space-y-1 text-xs">
                {excludedFolders.map((rule) => (
                  <li key={`${rule.mailboxId}/${rule.folderId}`} className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      {mailboxes.data?.find((mailbox) => mailbox.id === rule.mailboxId)?.address ??
                        excludedLabels.data?.find((label) => label.id === rule.mailboxId)?.address ??
                        rule.mailboxId}{" "}
                      /{" "}
                      {rule.mailboxId === mailboxId
                        ? (folders.data?.find((folder) => folder.id === rule.folderId)?.path ?? rule.folderId)
                        : (excludedLabels.data?.find(
                            (label) => label.id === rule.mailboxId && label.provider_folder_id === rule.folderId,
                          )?.path ?? rule.folderId)}
                    </span>
                    <button
                      type="button"
                      className="cursor-pointer text-muted-foreground underline"
                      disabled={!canUpdate}
                      onClick={() => setExcludedFolders((current) => current.filter((item) => item !== rule))}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-border/50">
            <Button
              variant="default"
              size="default"
              className="px-6 font-semibold shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
              onClick={handleSave}
              disabled={!canUpdate || updateMutation.isPending || isLoading || !!settingsError || !isFormValid}
            >
              {updateMutation.isPending ? (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <SaveIcon className="mr-2 h-4 w-4" />
              )}
              {updateMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!canUpdate && (
        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm">
          You don't have permission to update these settings. Please contact your administrator if you need to make
          changes.
        </div>
      )}
    </div>
  );
}
